import { isRelayablyPlanComingSoon } from '@monorepo/relayably/config'
import { Theme } from '@monorepo/relayably/theme'
import { RelayablyWorkspaceDocument } from '@monorepo/relayably/workspaces'
import React, { useEffect, useMemo, useState } from 'react'
import { ActivityIndicator, Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, useWindowDimensions, View } from 'react-native'
import { UserProfile } from '../../auth/types'
import { RelayablyPaymentProduct, UsageType } from '../../workspace/types'

export type SettingsSection = 'account' | 'company' | 'help'
export type SettingsActionState =
  | { type: 'idle' }
  | { type: 'checkout'; planId: UsageType }
  | { type: 'portal' }
  | { type: 'cancel' }

const hexToRgba = (hex: string, alpha: number) => {
  const value = hex.replace('#', '')
  const expanded =
    value.length === 3
      ? value
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : value
  const r = parseInt(expanded.slice(0, 2), 16)
  const g = parseInt(expanded.slice(2, 4), 16)
  const b = parseInt(expanded.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const splitDisplayPrice = (displayPrice: string) => {
  if (!displayPrice.startsWith('$')) {
    return {
      cents: '',
      dollars: displayPrice,
      hasCents: false,
      prefix: '',
    }
  }

  const [dollarsPart, centsPart] = displayPrice.slice(1).split('.')

  return {
    cents: centsPart ? `.${centsPart}` : '',
    dollars: dollarsPart,
    hasCents: Boolean(centsPart),
    prefix: '$',
  }
}

const BillingPrice = ({ displayPrice, interval, theme }: { displayPrice: string; interval: string; theme: Theme }) => {
  const styles = createStyles(theme)
  const price = splitDisplayPrice(displayPrice)

  return (
    <View style={styles.priceBadge}>
      {price.prefix ? <Text style={styles.pricePrefix}>{price.prefix}</Text> : null}
      <Text style={styles.priceDollars}>{price.dollars}</Text>
      {price.hasCents ? <Text style={styles.priceCents}>{price.cents}</Text> : null}
      <Text style={styles.priceInterval}>{`/${interval}`}</Text>
    </View>
  )
}

const formatPlanLabel = (value?: string | null) =>
  (value ?? 'Not set')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(token => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ')

const formatProviders = (providers: string[]) =>
  providers.length ? providers.map(provider => provider.charAt(0).toUpperCase() + provider.slice(1)).join(', ') : 'Not linked yet'

const formatBillingStatus = (billing: {
  status: string | null
  cancelAtPeriodEnd: boolean
  currentPeriodEnd: string | null
}) => {
  if (!billing.status) {
    return 'No active subscription recorded.'
  }

  const base = billing.status
    .split('_')
    .filter(Boolean)
    .map(token => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ')

  if (billing.cancelAtPeriodEnd) {
    const endDate = billing.currentPeriodEnd ? new Date(billing.currentPeriodEnd).toLocaleDateString() : 'the end of the current period'
    return `${base}. Cancels at ${endDate}.`
  }

  return base
}

const Field = ({
  label,
  value,
  onChangeText,
  theme,
  editable = true,
  multiline = false,
  placeholder,
}: {
  label: string
  value: string
  onChangeText?: (value: string) => void
  theme: Theme
  editable?: boolean
  multiline?: boolean
  placeholder?: string
}) => {
  const styles = createStyles(theme)

  if (!editable) {
    return (
      <View style={styles.fieldBlock}>
        <Text style={styles.inputLabel}>{label}</Text>
        <View style={styles.readOnlyField}>
          <Text style={styles.readOnlyValue}>{value || 'Not set'}</Text>
        </View>
      </View>
    )
  }

  return (
    <View style={styles.fieldBlock}>
      <Text style={styles.inputLabel}>{label}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        style={[styles.input, multiline ? styles.multiline : undefined]}
        multiline={multiline}
        placeholder={placeholder}
        placeholderTextColor={theme.colors.inactive}
      />
    </View>
  )
}

export const SettingsScreen = ({
  theme,
  user,
  workspace,
  billing,
  activeSection,
  onSectionChange,
  onLogout,
  onSaveAccount,
  onSaveCompanyProfile,
  billingProducts,
  billingLoading,
  workspaceLoading,
  billingError,
  actionState,
  onStartCheckout,
  onCancelSubscription,
  onOpenBillingPortal,
  onOpenPhoneForwardingGuide,
}: {
  theme: Theme
  user: UserProfile
  workspace: RelayablyWorkspaceDocument | null
  billing: {
    customerId: string | null
    subscriptionId: string | null
    planId: string | null
    status: string | null
    cancelAtPeriodEnd: boolean
    currentPeriodEnd: string | null
    canceledAt: string | null
  } | null
  activeSection: SettingsSection
  onSectionChange: (section: SettingsSection) => void
  onLogout: () => void
  onSaveAccount: (payload: { fullName: string; phoneNumber: string; workspaceName: string; country: string }) => void
  onSaveCompanyProfile: (payload: { businessName: string; website: string; businessDescription: string }) => void
  billingProducts: RelayablyPaymentProduct[]
  billingLoading: boolean
  workspaceLoading: boolean
  billingError: string | null
  actionState: SettingsActionState
  onStartCheckout: (planId: UsageType) => void
  onCancelSubscription: () => void
  onOpenBillingPortal: () => void
  onOpenPhoneForwardingGuide: () => void
}) => {
  const styles = createStyles(theme)
  const { width } = useWindowDimensions()
  const isCompact = width < 760
  const showCompanySection = workspace?.workspace.usageType ? workspace.workspace.usageType !== 'personal' : false
  const sections = useMemo(
    () =>
      [
        { key: 'account' as const, label: 'Account' },
        ...(showCompanySection ? [{ key: 'company' as const, label: 'Company Profile' }] : []),
        { key: 'help' as const, label: 'Help & Service' },
      ],
    [showCompanySection]
  )

  const normalizedSection = showCompanySection || activeSection !== 'company' ? activeSection : 'account'
  const [fullName, setFullName] = useState(user.fullName)
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? '')
  const [workspaceName, setWorkspaceName] = useState(workspace?.workspace.workspaceName ?? '')
  const [country, setCountry] = useState(workspace?.profile.country ?? '')
  const [businessName, setBusinessName] = useState(workspace?.workspace.businessName ?? '')
  const [website, setWebsite] = useState(workspace?.workspace.website ?? '')
  const [businessDescription, setBusinessDescription] = useState(workspace?.workspace.businessDescription ?? '')
  const [showCancelWarning, setShowCancelWarning] = useState(false)

  useEffect(() => {
    setFullName(user.fullName)
    setPhoneNumber(user.phoneNumber ?? '')
  }, [user.fullName, user.phoneNumber])

  useEffect(() => {
    setWorkspaceName(workspace?.workspace.workspaceName ?? '')
    setCountry(workspace?.profile.country ?? '')
    setBusinessName(workspace?.workspace.businessName ?? '')
    setWebsite(workspace?.workspace.website ?? '')
    setBusinessDescription(workspace?.workspace.businessDescription ?? '')
  }, [
    workspace?.profile.country,
    workspace?.workspace.businessDescription,
    workspace?.workspace.businessName,
    workspace?.workspace.website,
    workspace?.workspace.workspaceName,
  ])

  const selectedPlanId = billing?.planId ?? workspace?.workspace.usageType ?? null
  const hasScheduledCancellation = Boolean(billing?.cancelAtPeriodEnd)
  const screenDisabled = actionState.type !== 'idle'
  const planLoading = workspaceLoading || billingLoading
  const releaseDateLabel = billing?.currentPeriodEnd ? new Date(billing.currentPeriodEnd).toLocaleDateString() : 'the end of your current billing period'

  return (
    <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
      {isCompact ? (
        <View style={styles.mobileTabs}>
          {sections.map(section => (
            <Pressable
              key={section.key}
              disabled={screenDisabled}
              onPress={() => onSectionChange(section.key)}
              style={[styles.mobileTab, normalizedSection === section.key ? styles.mobileTabActive : undefined, screenDisabled ? styles.buttonDisabled : undefined]}
            >
              <Text style={[styles.mobileTabText, normalizedSection === section.key ? styles.mobileTabTextActive : undefined]}>
                {section.label}
              </Text>
            </Pressable>
          ))}
        </View>
      ) : null}

      {normalizedSection === 'account' ? (
        <View style={styles.stack}>
          <View style={styles.panel}>
            <Text style={styles.title}>Account</Text>
            <Text style={styles.supportingCopy}>Keep your personal details and workspace identity up to date here.</Text>
            <Field label='Full name' value={fullName} onChangeText={setFullName} theme={theme} placeholder='Your name' />
            <Field label='Email' value={user.email} theme={theme} editable={false} />
            <Field label='Phone number' value={phoneNumber} onChangeText={setPhoneNumber} theme={theme} placeholder='Add a contact number' />
            <Field label='Workspace name' value={workspaceName} onChangeText={setWorkspaceName} theme={theme} placeholder='Workspace name' />
            <Field label='Country' value={country} onChangeText={setCountry} theme={theme} placeholder='Country' />
            <View style={styles.readOnlyGrid}>
              <View style={styles.readOnlyCard}>
                <Text style={styles.inputLabel}>Providers</Text>
                <Text style={styles.readOnlyValue}>{formatProviders(user.linkedProviders)}</Text>
              </View>
              <View style={styles.readOnlyCard}>
                <Text style={styles.inputLabel}>Assigned number</Text>
                <Text style={styles.readOnlyValue}>{workspace?.workspace.phoneNumber?.phoneNumber ?? 'Not chosen yet'}</Text>
              </View>
            </View>
            <View style={styles.actionRow}>
              <Pressable
                disabled={screenDisabled}
                onPress={() => onSaveAccount({ fullName, phoneNumber, workspaceName, country })}
                style={[styles.primaryButton, screenDisabled ? styles.buttonDisabled : undefined]}
              >
                <Text style={styles.primaryButtonLabel}>Save account</Text>
              </Pressable>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.title}>Plan</Text>
            <Text style={styles.supportingCopy}>Manage your current Relayably plan here. If you want to cancel, use billing management below.</Text>
            {planLoading ? (
              <View style={styles.planLoadingCard}>
                <ActivityIndicator color={theme.colors.primary} size='small' />
                <Text style={styles.supportingCopy}>Loading your current plan and pricing…</Text>
              </View>
            ) : (
              <>
                <View style={styles.planStatusCard}>
                  <View style={styles.planStatusHeader}>
                    <Text style={styles.planName}>{formatPlanLabel(selectedPlanId)}</Text>
                    {selectedPlanId ? (
                      <View style={styles.planBadge}>
                        <Text style={styles.planBadgeText}>Current</Text>
                      </View>
                    ) : null}
                  </View>
                  <Text style={styles.itemBody}>{formatBillingStatus(billing ?? { status: null, cancelAtPeriodEnd: false, currentPeriodEnd: null })}</Text>
                </View>
                {billing?.cancelAtPeriodEnd ? (
                  <Text style={styles.warningText}>
                    Cancellation is already scheduled. Your number will be released on {releaseDateLabel}, and you should stop forwarding to it before then.
                  </Text>
                ) : null}
              </>
            )}
            {billingError ? <Text style={styles.errorText}>{billingError}</Text> : null}
            <View style={styles.stack}>
              {billingProducts.map(product => {
                const selected = selectedPlanId === product.id
                const selectedAndCanceling = selected && hasScheduledCancellation
                const selectedAndActive = selected && !hasScheduledCancellation
                const isPortalLoading = actionState.type === 'portal'
                const isCheckoutLoading = actionState.type === 'checkout' && actionState.planId === product.id
                const comingSoon = isRelayablyPlanComingSoon(product.id)

                return (
                  <View key={product.id} style={styles.itemCard}>
                    <View style={styles.itemHeader}>
                      <View style={styles.itemTitleRow}>
                        <Text style={styles.itemTitle}>{product.name}</Text>
                        {comingSoon ? (
                          <View style={styles.planBadgeMuted}>
                            <Text style={styles.planBadgeMutedText}>Coming Soon</Text>
                          </View>
                        ) : null}
                      </View>
                      <BillingPrice displayPrice={product.displayPrice} interval={product.interval} theme={theme} />
                    </View>
                    <Text style={styles.itemBody}>{product.description}</Text>
                    <View style={styles.featureWrap}>
                      {product.features.map(feature => (
                        <Text key={feature} style={styles.featureText}>
                          {`\u2022 ${feature}`}
                        </Text>
                      ))}
                    </View>
                    <View style={styles.billingActionRow}>
                      {selected ? (
                        <Text style={styles.selectedPlanText}>
                          {selectedAndCanceling ? `Cancels on ${releaseDateLabel}` : 'Current workspace plan'}
                        </Text>
                      ) : comingSoon ? (
                        <Text style={styles.selectedPlanText}>Business self-serve access is coming soon.</Text>
                      ) : (
                        <View />
                      )}
                      <View style={styles.billingButtonsGroup}>
                        {selectedAndActive ? (
                          <>
                            <Pressable disabled style={[styles.selectedPlanButton, styles.buttonDisabled]}>
                              <Text style={styles.selectedPlanButtonText}>Current Plan</Text>
                            </Pressable>
                            <Pressable
                              disabled={screenDisabled}
                              onPress={onOpenBillingPortal}
                              style={[styles.secondaryButton, screenDisabled ? styles.buttonDisabled : undefined]}
                            >
                              {isPortalLoading ? (
                                <ActivityIndicator color={theme.colors.text} size='small' />
                              ) : (
                                <Text style={styles.secondaryButtonText}>Manage plan or cancel</Text>
                              )}
                            </Pressable>
                          </>
                        ) : selectedAndCanceling ? (
                          <Pressable
                            disabled={screenDisabled}
                            onPress={onOpenBillingPortal}
                            style={[styles.primaryButton, screenDisabled ? styles.buttonDisabled : undefined]}
                          >
                            {isPortalLoading ? (
                              <ActivityIndicator color={theme.colors.staticWhite} size='small' />
                            ) : (
                              <Text style={styles.primaryButtonLabel}>Reopen this plan</Text>
                            )}
                          </Pressable>
                        ) : comingSoon ? (
                          <Pressable disabled style={[styles.secondaryButton, styles.buttonDisabled]}>
                            <Text style={styles.secondaryButtonText}>Coming Soon</Text>
                          </Pressable>
                        ) : (
                          <Pressable
                            disabled={screenDisabled}
                            onPress={() => onStartCheckout(product.id)}
                            style={[styles.primaryButton, screenDisabled ? styles.buttonDisabled : undefined]}
                          >
                            {isCheckoutLoading ? (
                              <ActivityIndicator color={theme.colors.staticWhite} size='small' />
                            ) : (
                              <Text style={styles.primaryButtonLabel}>Switch to this plan</Text>
                            )}
                          </Pressable>
                        )}
                      </View>
                    </View>
                  </View>
                )
              })}
            </View>
          </View>
        </View>
      ) : null}

      {normalizedSection === 'company' && showCompanySection ? (
        <View style={styles.panel}>
          <Text style={styles.title}>Company Profile</Text>
          <Text style={styles.supportingCopy}>This is the core company context Relayably uses when representing your business.</Text>
          <Field label='Business name' value={businessName} onChangeText={setBusinessName} theme={theme} placeholder='Business name' />
          <Field label='Website' value={website} onChangeText={setWebsite} theme={theme} placeholder='https://example.com' />
          <Field
            label='Description'
            value={businessDescription}
            onChangeText={setBusinessDescription}
            theme={theme}
            multiline
            placeholder='Describe the company, what it does, and what matters most.'
          />
          <View style={styles.actionRow}>
            <Pressable
              disabled={screenDisabled}
              onPress={() => onSaveCompanyProfile({ businessName, website, businessDescription })}
              style={[styles.primaryButton, screenDisabled ? styles.buttonDisabled : undefined]}
            >
              <Text style={styles.primaryButtonLabel}>Save company profile</Text>
            </Pressable>
          </View>
        </View>
      ) : null}

      {normalizedSection === 'help' ? (
        <View style={styles.stack}>
          <View style={styles.panel}>
            <Text style={styles.title}>Help & Service</Text>
            <Text style={styles.supportingCopy}>
              Need to recheck how to route your current phone into Relayably? Open the forwarding guide any time.
            </Text>
            <View style={styles.actionRow}>
              <Pressable
                disabled={screenDisabled}
                onPress={onOpenPhoneForwardingGuide}
                style={[styles.primaryButton, screenDisabled ? styles.buttonDisabled : undefined]}
              >
                <Text style={styles.primaryButtonLabel}>Open forwarding guide</Text>
              </Pressable>
            </View>
          </View>

          <Pressable disabled={screenDisabled} onPress={onLogout} style={[styles.logoutButton, screenDisabled ? styles.buttonDisabled : undefined]}>
            <Text style={styles.logoutLabel}>Log out</Text>
          </Pressable>
        </View>
      ) : null}

      <Modal transparent visible={showCancelWarning} animationType='fade' onRequestClose={() => setShowCancelWarning(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Cancel Relayably at period end?</Text>
            <Text style={styles.modalBody}>
              Your Relayably phone number will be released on {releaseDateLabel}. Stop forwarding calls to {workspace?.workspace.phoneNumber?.phoneNumber ?? 'your Relayably number'} before that date.
            </Text>
            <Text style={styles.modalBody}>
              We will notify you when cancellation is scheduled, one week before, one day before, and once your service has been fully disabled.
            </Text>
            <View style={styles.actionRow}>
              <Pressable
                disabled={screenDisabled}
                onPress={() => setShowCancelWarning(false)}
                style={[styles.secondaryButton, screenDisabled ? styles.buttonDisabled : undefined]}
              >
                <Text style={styles.secondaryButtonText}>Keep plan</Text>
              </Pressable>
              <Pressable
                disabled={screenDisabled}
                onPress={() => {
                  setShowCancelWarning(false)
                  onCancelSubscription()
                }}
                style={[styles.destructiveButton, screenDisabled ? styles.buttonDisabled : undefined]}
              >
                <Text style={styles.destructiveButtonText}>Continue cancellation</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    content: {
      gap: 16,
      paddingBottom: 24,
    },
    stack: {
      gap: 16,
    },
    panel: {
      borderRadius: 24,
      padding: 20,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 14,
    },
    title: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 28,
      lineHeight: 32,
    },
    supportingCopy: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    fieldBlock: {
      gap: 8,
    },
    inputLabel: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    },
    input: {
      borderRadius: 16,
      paddingHorizontal: 14,
      paddingVertical: 12,
      backgroundColor: hexToRgba(theme.colors.background, 0.72),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      color: theme.colors.text,
      fontSize: 14,
    },
    multiline: {
      minHeight: 120,
      textAlignVertical: 'top',
    },
    readOnlyField: {
      borderRadius: 16,
      paddingHorizontal: 14,
      paddingVertical: 12,
      backgroundColor: hexToRgba(theme.colors.background, 0.5),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
    },
    readOnlyValue: {
      color: theme.colors.text,
      fontSize: 14,
      lineHeight: 22,
    },
    readOnlyGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    readOnlyCard: {
      flex: 1,
      minWidth: 220,
      borderRadius: 18,
      padding: 14,
      backgroundColor: hexToRgba(theme.colors.background, 0.56),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 8,
    },
    mobileTabs: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    mobileTab: {
      borderRadius: 999,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
      backgroundColor: hexToRgba(theme.colors.surface, 0.84),
    },
    mobileTabActive: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.14),
      borderColor: hexToRgba(theme.colors.primary, 0.28),
    },
    mobileTabText: {
      color: theme.colors.inactive,
      fontSize: 13,
      fontWeight: '700',
    },
    mobileTabTextActive: {
      color: theme.colors.text,
    },
    planStatusCard: {
      borderRadius: 20,
      padding: 16,
      backgroundColor: hexToRgba(theme.colors.background, 0.56),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 10,
    },
    planLoadingCard: {
      alignItems: 'center',
      borderRadius: 20,
      padding: 20,
      backgroundColor: hexToRgba(theme.colors.background, 0.56),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 12,
      justifyContent: 'center',
      minHeight: 112,
    },
    planStatusHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    planName: {
      color: theme.colors.text,
      fontSize: 18,
      fontWeight: '700',
      flex: 1,
    },
    planBadge: {
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 5,
      backgroundColor: hexToRgba(theme.colors.primary, 0.14),
    },
    planBadgeText: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    itemCard: {
      borderRadius: 18,
      padding: 16,
      backgroundColor: hexToRgba(theme.colors.background, 0.56),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 8,
    },
    itemHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 10,
    },
    itemTitleRow: {
      alignItems: 'center',
      flex: 1,
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    itemTitle: {
      color: theme.colors.text,
      fontSize: 16,
      fontWeight: '700',
    },
    planBadgeMuted: {
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 5,
      backgroundColor: hexToRgba(theme.colors.inactive, 0.12),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.inactive, 0.16),
    },
    planBadgeMutedText: {
      color: theme.colors.inactive,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    itemBody: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    featureWrap: {
      gap: 6,
    },
    featureText: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
    priceBadge: {
      alignItems: 'flex-start',
      flexDirection: 'row',
      flexShrink: 0,
      gap: 1,
    },
    pricePrefix: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      marginTop: 1,
    },
    priceDollars: {
      color: theme.colors.primary,
      fontSize: 15,
      fontWeight: '800',
      lineHeight: 18,
    },
    priceCents: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '800',
      lineHeight: 14,
      marginTop: 1,
    },
    priceInterval: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      marginTop: 1,
    },
    actionRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    billingActionRow: {
      alignItems: 'center',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
      justifyContent: 'space-between',
    },
    billingButtonsGroup: {
      alignItems: 'center',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
      justifyContent: 'flex-end',
    },
    selectedPlanText: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    },
    primaryButton: {
      borderRadius: 999,
      paddingHorizontal: 18,
      paddingVertical: 12,
      backgroundColor: theme.colors.primary,
    },
    selectedPlanButton: {
      borderRadius: 999,
      paddingHorizontal: 18,
      paddingVertical: 12,
      backgroundColor: hexToRgba(theme.colors.primary, 0.14),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.18),
    },
    selectedPlanButtonText: {
      color: theme.colors.primary,
      fontSize: 14,
      fontWeight: '700',
    },
    primaryButtonLabel: {
      color: theme.colors.staticWhite,
      fontSize: 14,
      fontWeight: '700',
    },
    secondaryButton: {
      borderRadius: 999,
      paddingHorizontal: 18,
      paddingVertical: 12,
      backgroundColor: hexToRgba(theme.colors.background, 0.72),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
    },
    secondaryButtonText: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '700',
    },
    buttonDisabled: {
      opacity: 0.45,
    },
    errorText: {
      color: theme.colors.warning,
      fontSize: 13,
      lineHeight: 20,
    },
    warningText: {
      color: theme.colors.warning,
      fontSize: 13,
      lineHeight: 20,
    },
    modalBackdrop: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      backgroundColor: hexToRgba(theme.colors.background, 0.72),
    },
    modalCard: {
      width: '100%',
      maxWidth: 560,
      borderRadius: 24,
      padding: 22,
      gap: 14,
      backgroundColor: hexToRgba(theme.colors.surface, 0.98),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.14),
    },
    modalTitle: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 24,
      lineHeight: 28,
    },
    modalBody: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    logoutButton: {
      alignSelf: 'flex-start',
      borderRadius: 999,
      paddingHorizontal: 18,
      paddingVertical: 12,
      backgroundColor: hexToRgba(theme.colors.warning, 0.12),
    },
    logoutLabel: {
      color: theme.colors.warning,
      fontSize: 14,
      fontWeight: '700',
    },
  })
